import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { motion, useScroll, useTransform } from 'framer-motion';
import features, { partners } from './heroBannerData';
import './HeroBanner.scss';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: 'easeOut', delay },
  }),
};

export default function HeroBanner() {
  const containerRef = useRef(null);
  const [marqueePaused, setMarqueePaused] = useState(false);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  // ── Viewport-proportional calculations ──
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1920;

  // Card transforms: full-screen -> shrunk with rounded corners (stops at ~3 scrolls)
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.92]);
  const y = useTransform(scrollYProgress, [0, 0.5], [0, 10]);
  // ── Border sizes — proportional to viewport ──
  const BORDER_TOP    = Math.round(vw * 0.025);   // ~48px at 1920
  const BORDER_RIGHT  = Math.round(vw * 0.108);   // ~208px at 1920
  const BORDER_BOTTOM = Math.round(vw * 0.017);   // ~32px at 1920
  const BORDER_LEFT   = Math.round(vw * 0.025);   // ~48px at 1920

  const borderTop    = useTransform(scrollYProgress, [0, 0.4], [0, BORDER_TOP]);
  const borderRight  = useTransform(scrollYProgress, [0, 0.4], [0, BORDER_RIGHT]);
  const toolbarOpacity = useTransform(scrollYProgress, [0.25, 0.4], [0, 1]);
  const borderBottom = useTransform(scrollYProgress, [0, 0.4], [0, BORDER_BOTTOM]);
  const borderLeft   = useTransform(scrollYProgress, [0, 0.4], [0, BORDER_LEFT]);

  // White side gaps retract on scroll — proportional
  const wrapLeft  = useTransform(scrollYProgress, [0, 0.4], [Math.round(vw * 0.042), 0]);
  const wrapRight = useTransform(scrollYProgress, [0, 0.4], [Math.round(vw * 0.042), 0]);
  // Top retracts on scroll
  const wrapTop = useTransform(scrollYProgress, [0, 0.4], [112, 64]);

  return (
    <div ref={containerRef} className="hero-banner" style={{ height: '165vh', marginBottom: '25vh' }}>
      <div className="hero-banner__sticky">
        {/* Unified unit: card on top, video below, logos over video */}
        <motion.div
          className="hero-banner__card-wrap"
          style={{ scale, y, top: wrapTop, left: wrapLeft, right: wrapRight }}
          animate="visible"
          initial="hidden"
        >
          {/* Top bar — sits in the dark top border */}
          <motion.div className="hero-banner__topbar" style={{ opacity: toolbarOpacity }}>
            <div className="hero-banner__topbar-left">
              <img src="images/logo-icon-white.webp" alt="Weathersafe" className="hero-banner__topbar-logo" loading="lazy" />
              <span className="hero-banner__topbar-tab active">
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: '0.6em', height: '0.6em', marginRight: '0.2em' }}><rect x="3" y="3" width="10" height="10" rx="1"/></svg>
                Farmer Database
              </span>
              <span className="hero-banner__topbar-tab">Farmer Survey</span>
              <span className="hero-banner__topbar-tab">Activities Report</span>
            </div>
            <div className="hero-banner__topbar-right">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="4" width="12" height="8" rx="1"/><line x1="2" y1="7" x2="14" y2="7"/></svg>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 12V10H12V12"/><path d="M8 10V3"/><path d="M5 6L8 3L11 6"/></svg>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 8L8 3L13 8M13 8V13H3V8"/></svg>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="5,3 13,8 5,13"/></svg>
            </div>
          </motion.div>

          {/* Left icon bar — sits in the dark left border */}
          <motion.div className="hero-banner__iconbar" style={{ opacity: toolbarOpacity }}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="8" y1="3" x2="8" y2="13"/><line x1="3" y1="8" x2="13" y2="8"/></svg>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="2" width="10" height="12" rx="1"/><line x1="3" y1="5" x2="13" y2="5"/></svg>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="3" y1="4" x2="13" y2="4"/><line x1="3" y1="8" x2="13" y2="8"/><line x1="3" y1="12" x2="13" y2="12"/></svg>
            <div className="hero-banner__iconbar-divider" />
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="5"/><line x1="8" y1="3" x2="8" y2="1"/><line x1="8" y1="15" x2="8" y2="13"/><line x1="3" y1="8" x2="1" y2="8"/><line x1="15" y1="8" x2="13" y2="8"/></svg>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="2" width="5" height="5" rx="1"/><rect x="9" y="2" width="5" height="5" rx="1"/><rect x="2" y="9" width="5" height="5" rx="1"/><rect x="9" y="9" width="5" height="5" rx="1"/></svg>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="5" cy="8" r="2"/><circle cx="11" cy="8" r="2"/><line x1="7" y1="8" x2="9" y2="8"/></svg>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 8L8 3L13 8L8 13Z"/></svg>
            <div className="hero-banner__iconbar-divider" />
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 4L8 2L12 4V10L8 14L4 10Z"/></svg>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M2 12L8 4L14 12"/><line x1="5" y1="8" x2="11" y2="8"/></svg>
          </motion.div>

          {/* Right toolbar — sits in the dark border area */}
          <motion.div className="hero-banner__toolbar" style={{ opacity: toolbarOpacity }}>
            {/* Header */}
            <div className="hero-banner__toolbar-section">
              <div className="hero-banner__toolbar-row">
                <span className="hero-banner__toolbar-dot" style={{ background: '#ff5f57' }} />
                <span className="hero-banner__toolbar-dot" style={{ background: '#febc2e' }} />
                <span className="hero-banner__toolbar-dot" style={{ background: '#28c840' }} />
              </div>
              <div className="hero-banner__toolbar-heading">
                <span>H1</span>
                <span style={{ color: 'rgba(255,255,255,0.7)' }}>Origin Intelligence</span>
                <span style={{ color: 'rgba(255,255,255,0.2)' }}>···</span>
              </div>
              <div className="hero-banner__toolbar-tabs">
                <span className="active">Style</span>
                <span>Settings</span>
                <span>Data</span>
              </div>
            </div>
            <div className="hero-banner__toolbar-divider" />

            {/* Selector */}
            <div className="hero-banner__toolbar-section">
              <span className="hero-banner__toolbar-label">Station selector</span>
              <div className="hero-banner__toolbar-select">
                <span>Kigali North CWS</span>
                <span className="hero-banner__toolbar-chevron">▾</span>
              </div>
              <div className="hero-banner__toolbar-input-row">
                <div className="hero-banner__toolbar-input"><span>Zone</span><span>Region 4</span></div>
                <div className="hero-banner__toolbar-input"><span>ID</span><span>KN-042</span></div>
              </div>
            </div>
            <div className="hero-banner__toolbar-divider" />

            {/* Farm overview */}
            <div className="hero-banner__toolbar-section">
              <span className="hero-banner__toolbar-label">Farm overview</span>
              <div className="hero-banner__toolbar-toggle-group">
                <span className="active">All</span>
                <span>Active</span>
                <span>Flagged</span>
              </div>
              <div className="hero-banner__toolbar-input-row">
                <div className="hero-banner__toolbar-input"><span>Farms</span><span>342</span></div>
                <div className="hero-banner__toolbar-input"><span>Ha</span><span>1,280</span></div>
              </div>
              <div className="hero-banner__toolbar-input-row">
                <div className="hero-banner__toolbar-input"><span>Trees</span><span>485k</span></div>
                <div className="hero-banner__toolbar-input"><span>Avg age</span><span>8yr</span></div>
              </div>
            </div>
            <div className="hero-banner__toolbar-divider" />

            {/* Yield */}
            <div className="hero-banner__toolbar-section">
              <div className="hero-banner__toolbar-row" style={{ justifyContent: 'space-between' }}>
                <span className="hero-banner__toolbar-label" style={{ marginBottom: 0 }}>Yield forecast</span>
                <span style={{ color: 'rgba(255,255,255,0.2)', cursor: 'default' }}>▾</span>
              </div>
              <div className="hero-banner__toolbar-yield-bar">
                <div className="hero-banner__toolbar-yield-fill" />
              </div>
              <div className="hero-banner__toolbar-input-row">
                <div className="hero-banner__toolbar-input"><span>Est</span><span>48t</span></div>
                <div className="hero-banner__toolbar-input"><span>Pot</span><span>72t</span></div>
              </div>
              <div className="hero-banner__toolbar-input-row">
                <div className="hero-banner__toolbar-input"><span>Gap</span><span className="hero-banner__toolbar-badge">33%</span></div>
                <div className="hero-banner__toolbar-input"><span>Kg/tree</span><span>4.0</span></div>
              </div>
            </div>
            <div className="hero-banner__toolbar-divider" />

            {/* Health */}
            <div className="hero-banner__toolbar-section">
              <span className="hero-banner__toolbar-label">Health &amp; alerts</span>
              <div className="hero-banner__toolbar-health-row">
                <div className="hero-banner__toolbar-health-dot good" />
                <span style={{ color: 'rgba(255,255,255,0.6)' }}>Farm health</span>
                <span className="hero-banner__toolbar-badge">Good</span>
              </div>
              <div className="hero-banner__toolbar-input-row">
                <div className="hero-banner__toolbar-input"><span>Alerts</span><span>16M+</span></div>
                <div className="hero-banner__toolbar-input"><span>Pest</span><span>Low</span></div>
              </div>
              <div className="hero-banner__toolbar-input-row">
                <div className="hero-banner__toolbar-input"><span>Satellite</span><span>Weekly</span></div>
                <div className="hero-banner__toolbar-input"><span>Weather</span><span>94%</span></div>
              </div>
            </div>
            <div className="hero-banner__toolbar-divider" />

            {/* Season */}
            <div className="hero-banner__toolbar-section">
              <span className="hero-banner__toolbar-label">Season</span>
              <div className="hero-banner__toolbar-input-row">
                <div className="hero-banner__toolbar-input"><span>Harvest</span><span>Apr–Jul</span></div>
                <div className="hero-banner__toolbar-input"><span>Peak</span><span>May</span></div>
              </div>
              <div className="hero-banner__toolbar-input-row">
                <div className="hero-banner__toolbar-input"><span>Beds</span><span>12/18</span></div>
                <div className="hero-banner__toolbar-input"><span>C:G</span><span>7:1</span></div>
              </div>
            </div>
            <div className="hero-banner__toolbar-divider" />

            {/* Processing */}
            <div className="hero-banner__toolbar-section">
              <span className="hero-banner__toolbar-label">Processing</span>
              <div className="hero-banner__toolbar-select">
                <span>Natural dry</span>
                <span className="hero-banner__toolbar-chevron">▾</span>
              </div>
              <div className="hero-banner__toolbar-input-row">
                <div className="hero-banner__toolbar-input"><span>Moisture</span><span>11.2%</span></div>
                <div className="hero-banner__toolbar-input"><span>Target</span><span>&lt;9%</span></div>
              </div>
            </div>
          </motion.div>

          {/* Bottom breadcrumb bar — sits in the dark bottom border */}
          <motion.div className="hero-banner__breadcrumb" style={{ opacity: toolbarOpacity }}>
            <div className="hero-banner__breadcrumb-icons">
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="5"/><path d="M6.5 6.5L9.5 9.5M9.5 6.5L6.5 9.5"/></svg>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="8" cy="8" r="2"/><path d="M8 3V1M8 15V13M13 8H15M1 8H3"/></svg>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 10L8 5L13 10"/></svg>
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="10" height="10" rx="1"/><path d="M3 7H13"/></svg>
            </div>
            <div className="hero-banner__breadcrumb-trail">
              <span className="hero-banner__breadcrumb-item">□ Body</span>
              <span className="hero-banner__breadcrumb-sep">›</span>
              <span className="hero-banner__breadcrumb-item">□ Origin</span>
              <span className="hero-banner__breadcrumb-sep">›</span>
              <span className="hero-banner__breadcrumb-item">□ Farm Maps</span>
              <span className="hero-banner__breadcrumb-sep">›</span>
              <span className="hero-banner__breadcrumb-item">□ GAP77</span>
              <span className="hero-banner__breadcrumb-sep">›</span>
              <span className="hero-banner__breadcrumb-item active">□ Alerts</span>
            </div>
          </motion.div>

          {/* Inner content — inset animates to reveal dark border behind it */}
          <motion.div
            className="hero-banner__card-inner"
            style={{ top: borderTop, right: borderRight, bottom: borderBottom, left: borderLeft }}
          >
          {/* White card — top portion */}
          <div className="hero-banner__card hero-banner__card--no-bg">
            <Container>
              <motion.h1
                className="hero-banner__title"
                variants={fadeUp}
                custom={0.1}
              >
                Know your origin.<br /><span>Claims end. Evidence starts.</span>
              </motion.h1>
              <motion.p
                className="hero-banner__subtitle"
                variants={fadeUp}
                custom={0.25}
              >
                Build traceability. Plan procurement. Prove impact.
              </motion.p>

              <motion.div
                className="hero-banner__labels-row"
                variants={fadeUp}
                custom={0.5}
              >
                <div className="hero-banner__scale-row">
                  <h2 className="hero-banner__features-heading">Building at scale?</h2>
                  <Link to="/contact-us-solution" className="hero-banner__scale-btn">Talk to Sales</Link>
                </div>
              </motion.div>
              <div className="hero-banner__features">
                {features.map((f, idx) => (
                  <motion.div
                    key={f.id}
                    variants={fadeUp}
                    custom={0.55 + idx * 0.15}
                  >
                    <Link to={f.href} className="hero-banner__feature-box">
                      <div className="hero-banner__feature-text">
                        <h3 className="hero-banner__feature-title">
                          {f.title}
                          {f.id === 'optimise' && <span className="hero-banner__feature-badge">new</span>}
                        </h3>
                        <p className="hero-banner__feature-desc">{f.description}</p>
                      </div>
                      {f.id === 'map' && (
                        <div className="hero-banner__feature-ui-overlay">
                          <span className="hero-banner__feature-ui-text">Map the Farms</span>
                          <hr className="hero-banner__feature-ui-divider" />
                          <div className="hero-banner__feature-ui-buttons">
                            <span className="hero-banner__feature-ui-btn-grey">info</span>
                            <span className="hero-banner__feature-ui-btn-grey">no</span>
                            <span className="hero-banner__feature-ui-btn-green-wrap">
                              <span className="hero-banner__feature-ui-btn-green">yes</span>
                              <svg className="hero-banner__feature-ui-cursor" width="14" height="18" viewBox="0 0 14 18" fill="none">
                                <path d="M1 1L1 13.5L4.5 10.5L7.5 17L9.5 16L6.5 9.5L11 9.5L1 1Z" fill="#fff" stroke="#000" strokeWidth="1.2" />
                              </svg>
                            </span>
                            <span className="hero-banner__feature-ui-btn-grey">survey</span>
                          </div>
                        </div>
                      )}
                      {f.id === 'optimise' && (
                        <div className="hero-banner__feature-ui-overlay">
                          <span className="hero-banner__feature-ui-text">Survey Farmers</span>
                          <hr className="hero-banner__feature-ui-divider" />
                          <div className="hero-banner__feature-ui-buttons">
                            <span className="hero-banner__feature-ui-btn-grey">zone</span>
                            <span className="hero-banner__feature-ui-btn-grey">skip</span>
                            <span className="hero-banner__feature-ui-btn-green-wrap">
                              <span className="hero-banner__feature-ui-btn-green">start</span>
                              <svg className="hero-banner__feature-ui-cursor" width="14" height="18" viewBox="0 0 14 18" fill="none">
                                <path d="M1 1L1 13.5L4.5 10.5L7.5 17L9.5 16L6.5 9.5L11 9.5L1 1Z" fill="#fff" stroke="#000" strokeWidth="1.2" />
                              </svg>
                            </span>
                            <span className="hero-banner__feature-ui-btn-grey">report</span>
                          </div>
                        </div>
                      )}
                      <div className="hero-banner__feature-img-wrap">
                        <img
                          src={f.image}
                          alt={f.title}
                          className="hero-banner__feature-img"
                          loading="lazy"
                        />
                        <div className="hero-banner__feature-frost" />
                        <div className="hero-banner__feature-arrow">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="12 5 19 12 12 19" />
                          </svg>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </Container>
            <motion.div
              className="hero-banner__card-bg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.85 }}
            />
          </div>

          {/* Video — directly below the card, same width */}
          <div className="hero-banner__video-placeholder">
            <video
              className="hero-banner__video"
              src="https://assets.mixkit.co/videos/41859/41859-1080.mp4"
              autoPlay
              loop
              muted
              playsInline
            />

          {/* Logo bar — overlays top of video (bottom of card) */}
          <div className="hero-banner__logo-marquee">
            <button
              className="hero-banner__marquee-toggle"
              onClick={() => setMarqueePaused((p) => !p)}
              aria-label={marqueePaused ? 'Play carousel' : 'Pause carousel'}
            >
              {marqueePaused ? '\u25B6' : '\u275A\u275A'}
            </button>
            <div className="hero-banner__logo-marquee-inner">
              <div className={`hero-banner__logo-track${marqueePaused ? ' hero-banner__logo-track--paused' : ''}`}>
                {[...Array(6)].map((_, setIdx) => (
                  <React.Fragment key={setIdx}>
                    {partners.map((p) => {
                      const uid = `${setIdx}-${p.id}`;
                      return (
                        <span key={uid} className="hero-banner__logo-item">
                          <img src={p.image} alt={p.alt} className="hero-banner__logo" loading="lazy" />
                        </span>
                      );
                    })}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
          </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
